import { useState, useEffect, useCallback } from 'react';
import { staffAPI } from '../services/staff';
import { useApi } from '../../../hooks/api/useApi';

export function useStaff() {
  const [staff, setStaff] = useState([]);
  const [currentStaff, setCurrentStaff] = useState(null);
  const { loading, error, execute, clearError } = useApi();

  const fetchDepartmentStaff = useCallback(async (params = {}) => {
    const result = await execute(() => staffAPI.getDepartmentStaff(params));
    if (result.success) {
      setStaff(result.data.staff_members || []);
    }
    return result;
  }, [execute]);

  const fetchCurrentStaff = useCallback(async () => {
    const result = await execute(() => staffAPI.getCurrentStaff());
    if (result.success) {
      setCurrentStaff(result.data);
    }
    return result;
  }, [execute]);

  const changeCurrentStaffPassword = useCallback(async (currentPassword, newPassword) => {
    const result = await execute(() => staffAPI.changeCurrentStaffPassword({
      current_password: currentPassword,
      new_password: newPassword
    }));
    return result;
  }, [execute]);

  const setCurrentStaffPin = useCallback(async (currentPassword, pin) => {
    return await execute(() => staffAPI.setCurrentStaffPin({
      current_password: currentPassword,
      pin,
    }));
  }, [execute]);

  const disableCurrentStaffPin = useCallback(async (currentPassword) => {
    return await execute(() => staffAPI.disableCurrentStaffPin({
      current_password: currentPassword,
    }));
  }, [execute]);

  const dismissCurrentStaffPinPrompt = useCallback(async () => {
    return await execute(() => staffAPI.dismissCurrentStaffPinPrompt());
  }, [execute]);

  const createStaff = useCallback(async (data) => {
    const result = await execute(() => staffAPI.createStaff(data));
    if (result.success) {
      // Refresh the staff list
      await fetchDepartmentStaff();
    }
    return result;
  }, [execute, fetchDepartmentStaff]);

  const updateStaff = useCallback(async (staffId, data) => {
    const result = await execute(() => staffAPI.updateStaff(staffId, data));
    if (result.success) {
      // Update the staff in the list
      setStaff(prev => prev.map(s =>
        s.staff_member_id === staffId ? { ...s, ...result.data } : s
      ));
    }
    return result;
  }, [execute]);

  const deleteStaff = useCallback(async (staffId) => {
    const result = await execute(() => staffAPI.deleteStaff(staffId));
    if (result.success) {
      // Remove from the staff list
      setStaff(prev => prev.filter(s => s.staff_member_id !== staffId));
    }
    return result;
  }, [execute]);

  const assignRole = useCallback(async (staffId, roleId) => {
    return await execute(() => staffAPI.assignRole(staffId, { role_id: roleId }));
  }, [execute]);

  const removeRole = useCallback(async (staffId, roleId) => {
    return await execute(() => staffAPI.removeRole(staffId, roleId));
  }, [execute]);

  const getStaffRoles = useCallback(async (staffId) => {
    return await execute(() => staffAPI.getStaffRoles(staffId));
  }, [execute]);

  const fetchStaffFiles = useCallback(async (staffId) => {
    return await execute(() => staffAPI.listStaffFiles(staffId));
  }, [execute]);

  const attachStaffFile = useCallback(async (staffId, fileId) => {
    return await execute(() => staffAPI.attachStaffFile(staffId, fileId));
  }, [execute]);

  const removeStaffFile = useCallback(async (staffId, staffMemberFileId) => {
    return await execute(() => staffAPI.removeStaffFile(staffId, staffMemberFileId));
  }, [execute]);

  const setColor = useCallback(async (color) => {
    return await execute(() => staffAPI.setColor(color));
  }, [execute]);

  return {
    staff,
    currentStaff,
    loading,
    error,
    clearError,
    fetchDepartmentStaff,
    fetchCurrentStaff,
    changeCurrentStaffPassword,
    setCurrentStaffPin,
    disableCurrentStaffPin,
    dismissCurrentStaffPinPrompt,
    createStaff,
    updateStaff,
    deleteStaff,
    assignRole,
    removeRole,
    getStaffRoles,
    fetchStaffFiles,
    attachStaffFile,
    removeStaffFile,
    setColor,
  };
}